import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { ChevronRight, MessageCircle, Calendar, ShoppingBag } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import Carousel from "../components/Carousel";
import InstallPrompt from "../components/InstallPrompt";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { vipTextColor, vipBadgeBg } from "../lib/vipColors";
import { resolveImageUrl } from "../lib/imageUrl";
import { useRefreshOnResume } from "../hooks/useRefreshOnResume";
import { useTablet } from "../hooks/useTablet";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const WA = "https://wa.me/77018017755";

const VIP_CFG = {
  Diamond: { color: "#B9F2FF", bg: "rgba(13,27,62,0.8)", border: "#4FC3F7" },
  Platinum: { color: "#E8E8E8", bg: "rgba(40,40,40,0.8)", border: "#C0C0C0" },
  Gold: { color: "var(--ang-gold)", bg: "rgba(42,31,10,0.8)", border: "var(--ang-gold)" },
  Silver: { color: "#C0C0C0", bg: "rgba(30,30,30,0.8)", border: "var(--ang-text-muted)" },
  Standard: { color: "var(--ang-text-muted)", bg: "rgba(20,20,20,0.8)", border: "#444" },
};

function VipBadge({ level, onDark = false }) {
  const { theme } = useTheme();
  const base = VIP_CFG[level] || VIP_CFG.Standard;
  // На тёмной VIP-карте значок остаётся «ночным» даже в светлой теме.
  const light = theme === "light" && !onDark;
  const cfg = light
    ? { ...base, color: vipTextColor(level, "light", base.color), bg: vipBadgeBg(level, "light", base.bg) }
    : base;
  return (
    <span data-testid="vip-badge" style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 10px", borderRadius: "2px" }}>
      {level}
    </span>
  );
}
import { useSeo } from "../lib/seo";

export default function Dashboard() {
  useSeo({
    title: "ANGIOLINI VIP CLUB — итальянская верхняя одежда в Алматы",
    description: "Бутик ANGIOLINI в Алматы: женские пуховики, кашемировые пальто, модели с мехом и шубы итальянских марок. Каталог с ценами и бонусная программа VIP Club.",
    canonical: "/",
  });
  const { tablet, cols } = useTablet();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, tField } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [news, setNews] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);

  const load = useCallback(async () => {
    // allSettled — одна упавшая ручка (например 401 /bonus/balance) не должна
    // обнулять весь экран. Каждый блок выставляется независимо.
    const [b, n, p, br] = await Promise.allSettled([
      axios.get(`${API}/bonus/balance`, { withCredentials: true }),
      axios.get(`${API}/news`),
      axios.get(`${API}/products?is_new=true`),
      axios.get(`${API}/brands`),
    ]);
    if (b.status === "fulfilled") setBalance(b.value.data);
    if (n.status === "fulfilled") setNews(n.value.data.slice(0, 3));
    if (p.status === "fulfilled") setProducts(p.value.data.slice(0, 4));
    if (br.status === "fulfilled") {
      // Показываем все активные бренды; featured — первыми (приоритет в порядке).
      // Раньше: если был хоть один featured — показывали ТОЛЬКО его, и карусель
      // казалась сломанной (Nello Santi один в карусели на 6 брендов).
      const all = (br.value.data || []).filter(b => b.is_active !== false);
      const sorted = [...all].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
      setBrands(sorted.slice(0, 12));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Возврат в приложение из фона — обновляем новинки, бонусы и новости.
  useRefreshOnResume(load);


  const firstName = user?.name?.split(" ")[0] || "Гость";
  const vipLevel = balance?.vip_level ?? user?.vip_level ?? "Silver";
  const bonusBalance = balance?.balance ?? balance?.bonus_balance ?? 0;
  const lockedBonus = balance?.locked ?? 0;
  const totalPurchases = balance?.total_purchases ?? user?.total_purchases ?? 0;
  const { theme } = useTheme();
  const cfgBase = VIP_CFG[vipLevel] || VIP_CFG.Standard;
  const cfg = { ...cfgBase, color: vipTextColor(vipLevel, theme, cfgBase.color) };

  // VIP пороги — ЗЕРКАЛО backend/bonuses.py:VIP_THRESHOLDS
  // Silver: 0..1M  →  Gold: 1M..5M  →  Platinum: 5M..15M  →  Diamond: 15M+
  const NEXT_THRESHOLD = { Silver: 1000000, Gold: 5000000, Platinum: 15000000, Diamond: null, Standard: 1000000 };
  const nextThreshold = NEXT_THRESHOLD[vipLevel];
  const progress = nextThreshold ? Math.min((totalPurchases / nextThreshold) * 100, 100) : 100;

  return (
    <div data-testid="client-dashboard" style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ padding: "56px 20px 24px", background: "linear-gradient(180deg,var(--ang-bg-top) 0%,var(--ang-bg) 100%)" }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", marginBottom: "6px" }}>{t.dashboard.welcome}</div>
          <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "30px", fontWeight: 400, color: "var(--ang-text)", margin: "0 0 10px" }}>
            {user ? firstName : "ANGIOLINI VIP CLUB"}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {user && <VipBadge level={vipLevel} />}
            <ThemeToggle />
          </div>
        </motion.div>
      </div>

      <InstallPrompt />

      {/* Bonus Card Preview */}
      {!user && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="guest-vip-invite" onClick={() => navigate("/auth")}
          style={{ margin: "0 16px 20px", background: "var(--ang-card-bg)", borderRadius: "8px", padding: "22px",
            border: "1px solid var(--ang-card-border)", boxShadow: "var(--ang-shadow)", cursor: "pointer" }}>
          <div style={{ fontFamily: "Italiana", fontSize: "20px", color: "var(--ang-gold-2)", marginBottom: "10px" }}>Angiolini VIP Club</div>
          <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-card-muted)", lineHeight: 1.7, marginBottom: "16px" }}>
            Каталог, бренды и новости открыты без регистрации. Бонусная карта с кэшбэком до 5%, история покупок
            и AI-примерка — для участников клуба.
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--ang-gold)",
            color: "#1A1A1A", borderRadius: "999px", padding: "11px 20px", fontFamily: "Manrope",
            fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Войти / Получить VIP-привилегии <ChevronRight size={13} />
          </div>
        </motion.div>
      )}
      {user && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        onClick={() => navigate("/app/bonus")} data-testid="bonus-card-preview"
        style={{ margin: "0 16px 20px", background: "var(--ang-card-bg)", borderRadius: "8px", padding: "20px", border: "1px solid var(--ang-card-border)", boxShadow: "var(--ang-shadow)", cursor: "pointer", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "-20px", right: "0", width: "100px", height: "100px", borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
          <div style={{ fontFamily: "Italiana", fontSize: "20px", color: "var(--ang-gold-2)" }}>Angiolini</div>
          <VipBadge level={vipLevel} onDark />
        </div>
        <div data-testid="bonus-balance" style={{ fontFamily: "Cormorant Garamond", fontSize: "38px", fontWeight: 600, color: "var(--ang-gold-2)", lineHeight: 1 }}>
          {Math.floor(bonusBalance).toLocaleString("ru-RU")}
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-card-muted)", letterSpacing: "0.1em", marginBottom: lockedBonus > 0 ? "6px" : "16px" }}>{t.dashboard.bonuses} · доступно</div>
        {lockedBonus > 0 && (
          <div data-testid="dashboard-locked-hint" style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold)", background: "var(--ang-gold-soft)", border: "1px dashed var(--ang-gold-line)", padding: "2px 7px", borderRadius: "3px", marginBottom: "16px", letterSpacing: "0.05em" }}>
            + {Math.floor(lockedBonus).toLocaleString("ru-RU")} заморожено
          </div>
        )}
        <div style={{ background: "rgba(255,255,255,0.14)", borderRadius: "2px", height: "3px", marginBottom: "8px" }}>
          <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg,var(--ang-gold-2),rgba(212,184,120,0.55))", borderRadius: "2px", transition: "width 1s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-card-muted)" }}>
            {t.dashboard.purchasesShort}: {(totalPurchases).toLocaleString("ru-RU")} ₸
          </span>
          <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold-2)", display: "flex", alignItems: "center", gap: "4px" }}>
            {t.dashboard.details} <ChevronRight size={12} />
          </span>
        </div>
      </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
        style={{ padding: "0 16px 20px", display: "grid", gridTemplateColumns: tablet ? "repeat(3, minmax(0,1fr))" : "1fr 1fr 1fr", gap: tablet ? "16px" : "10px", maxWidth: tablet ? "720px" : undefined }}>
        {[
          { icon: Calendar, label: t.dashboard.appointment, action: () => navigate("/app/appointment"), testid: "quick-appointment" },
          { icon: MessageCircle, label: t.dashboard.selectStyle, action: () => window.open(WA, "_blank"), testid: "quick-whatsapp" },
          { icon: ShoppingBag, label: t.dashboard.catalog, action: () => navigate("/app/catalog"), testid: "quick-catalog" },
        ].map(({ icon: Icon, label, action, testid }) => (
          <button key={testid} data-testid={testid} onClick={action}
            style={{ background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "6px", padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <Icon size={20} style={{ color: "var(--ang-gold)" }} />
            <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-soft)", textAlign: "center", lineHeight: 1.4 }}>{label}</span>
          </button>
        ))}
      </motion.div>

      {/* News & Promos */}
      {news.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          style={{ padding: "0 16px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400 }}>{t.dashboard.newsPromos}</h2>
            <button onClick={() => navigate("/app/news")} style={{ background: "none", border: "none", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              {t.dashboard.all} <ChevronRight size={14} />
            </button>
          </div>
          <Carousel
            testId="news-carousel"
            items={news}
            perPage={4}
            gap={12}
            renderItem={(item) => (
              <div onClick={() => navigate("/app/news")} data-testid={`news-card-${item.id}`}
                style={{ background: "var(--ang-surface)", borderRadius: "6px", overflow: "hidden", cursor: "pointer" }}>
                {item.image_url && (
                  <div style={{ width: "100%", aspectRatio: "3/4", background: "var(--ang-surface-2)", overflow: "hidden" }}>
                    <img src={resolveImageUrl(item.image_url, { w: 800 })} alt={tField(item, "title")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                )}
                <div style={{ padding: "12px" }}>
                  {item.is_vip_only && <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-gold)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>{t.dashboard.vipExclusive}</div>}
                  <div style={{ fontFamily: "Cormorant Garamond", fontSize: "15px", color: "var(--ang-text)", lineHeight: 1.3 }}>{tField(item, "title")}</div>
                </div>
              </div>
            )}
          />
        </motion.div>
      )}

      {/* Featured Brands */}
      {brands.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}
          style={{ padding: "0 16px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400 }}>Бренды</h2>
            <button data-testid="all-brands-link" onClick={() => navigate("/app/catalog?tab=brands")}
              style={{ background: "none", border: "none", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              Все бренды <ChevronRight size={14} />
            </button>
          </div>
          <Carousel
            testId="brands-carousel"
            items={brands}
            perPage={4}
            gap={10}
            renderItem={(b) => (
              <div onClick={() => navigate(`/app/brand/${b.slug}`)} data-testid={`brand-tile-${b.slug}`}
                style={{ background: "var(--ang-surface)", borderRadius: "6px", overflow: "hidden", cursor: "pointer", border: "1px solid var(--ang-border)" }}>
                <div style={{ width: "100%", aspectRatio: "3/4", background: "var(--ang-surface-2)", position: "relative", overflow: "hidden" }}>
                  {b.cover_image ? (
                    <img src={resolveImageUrl(b.cover_image, { w: 600 })} alt={tField(b, "name")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-gold-strong)", fontFamily: "Italiana", fontSize: "36px" }}>
                      {tField(b, "name")?.[0] || "A"}
                    </div>
                  )}
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.85) 100%)" }} />
                  <div style={{ position: "absolute", left: 10, right: 10, bottom: 8 }}>
                    <div style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: "var(--ang-text)", fontWeight: 500, lineHeight: 1.1 }}>{tField(b, "name")}</div>
                    <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-gold-strong)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 3 }}>
                      {b.country || ""}{b.founded_year ? ` · ${b.founded_year}` : ""}
                    </div>
                  </div>
                </div>
              </div>
            )}
          />
        </motion.div>
      )}

      {/* New Arrivals */}
      {products.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
          style={{ padding: "0 16px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400 }}>{t.dashboard.newArrivals}</h2>
            <button onClick={() => navigate("/app/catalog")} style={{ background: "none", border: "none", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              {t.dashboard.all} <ChevronRight size={14} />
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: tablet ? "16px" : "10px" }}>
            {products.map((p) => (
              <div key={p.id} onClick={() => navigate(`/app/catalog/${p.id}`)} data-testid={`product-card-${p.id}`}
                style={{ background: "var(--ang-surface)", borderRadius: "6px", overflow: "hidden", cursor: "pointer" }}>
                <div style={{ position: "relative", aspectRatio: "9/16", background: "var(--ang-surface-2)", overflow: "hidden" }}>
                  <img src={resolveImageUrl(p.images?.[0], { w: 600 })} alt={tField(p, "name")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  {p.discount && <div style={{ position: "absolute", top: "10px", right: "10px", background: "#E11D2A", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "12px", fontWeight: 900, padding: "5px 9px", borderRadius: "3px", boxShadow: "0 2px 8px rgba(225,29,42,0.45)", border: "1px solid var(--ang-border-strong)" }}>-{p.discount}%</div>}
                </div>
                <div style={{ padding: "10px" }}>
                  <div style={{ fontFamily: "Cormorant Garamond", fontSize: "13px", color: "var(--ang-text)", lineHeight: 1.3, marginBottom: "4px" }}>{tField(p, "name")}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
                    {p.old_price && <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", textDecoration: "line-through" }}>{(p.old_price).toLocaleString("ru-RU")} ₸</span>}
                    <span style={{ fontFamily: "Manrope", fontSize: "13px", color: p.old_price ? "#E11D2A" : "var(--ang-gold)", fontWeight: 700 }}>{(p.price).toLocaleString("ru-RU")} ₸</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* WhatsApp CTA */}
      <div style={{ padding: "0 16px 32px" }}>
        <button data-testid="whatsapp-cta" onClick={() => { trackEvent("whatsapp_click", { page: "/app/home" }); window.open(WA, "_blank"); }}
          style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: "6px", padding: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          <span style={{ fontFamily: "Manrope", fontSize: "14px", color: "#25D366", fontWeight: 600 }}>{t.dashboard.whatsapp}</span>
        </button>
      </div>

      {/* Информационные разделы — полезны клиентам и доступны поиску */}
      <nav data-testid="home-info-links" style={{ padding: "0 16px 36px", display: "flex", flexWrap: "wrap", gap: "14px", justifyContent: "center" }}>
        {[["about", "О бутике"], ["contacts", "Контакты"], ["delivery", "Доставка и оплата"], ["returns", "Обмен и возврат"]].map(([slug, label]) => (
          <Link key={slug} to={`/info/${slug}`} data-testid={`home-info-${slug}`}
            style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-faint)", textDecoration: "none", letterSpacing: "0.03em" }}>
            {label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
